import { CONTRIBUTIONS, CURRENCIES_SIGNS } from 'utils/constants';
import { ICurrencyFormat } from 'utils/types';
import numeral from 'numeral';
import { IChartOptionsConfig } from 'views/Products/types';

const COLUMN_WIDTH = 256;
const SPEND_BY_PRODUCT_WIDTH = 164;

const currencyFormatter = (format: ICurrencyFormat, value: number) =>
  Boolean(value)
    ? `${CURRENCIES_SIGNS[format.currency]}${numeral(value).format('0,0.[0]')}`
    : '-';

const percentageFormatter = (value: number) =>
  Boolean(value) ? numeral(value / 100).format('0,0.[00]%') : '-';

const getHeadcountColumn = () => ({
  headerName: 'Total Headcount',
  field: 'headcount',
  type: 'numericColumn',
  sortable: true,
});

const getSpendColumn = (format: ICurrencyFormat) => ({
  headerName: 'Cost',
  field: 'spend',
  type: 'numericColumn',
  sortable: true,
  valueFormatter: (params: any) => currencyFormatter(format, params.value),
});

export const useTableData = (
  chartOptionsConfig: IChartOptionsConfig,
  format: ICurrencyFormat,
  isHeadcount: boolean
) => {
  const { contributions, level1, level2, level3 } = chartOptionsConfig;
  const isPersonnel = contributions === CONTRIBUTIONS.PERSONNEL;
  const isThirdParty = contributions === CONTRIBUTIONS.THIRD_PARTY;
  if (level3) {
    return getSpendByProductColumns({
      contributions,
      format,
      isHeadcount,
      title: 'Service tower l2',
    });
  } else if (level2) {
    let title = 'Service tower l1 / Job role';
    if (isPersonnel) {
      title = 'Job role';
    } else if (isThirdParty) {
      title = 'Service tower l1 name';
    }
    return getSpendByProductColumns({
      contributions,
      format,
      isHeadcount,
      title,
    });
  } else if (level1) {
    return getSpendByProductColumns({
      contributions,
      format,
      isHeadcount,
      title: 'Application name',
    });
  }
  return getProductsColumns(format, isHeadcount);
};

const getProductsColumns = (format: ICurrencyFormat, isHeadcount: boolean) => {
  const valueColumn = isHeadcount
    ? getHeadcountColumn()
    : getSpendColumn(format);
  return [
    {
      headerName: 'Product Name',
      field: 'name',
      minWidth: 312,
      sortable: true,
    },
    {
      headerName: 'Revenue',
      field: 'productRevenue',
      minWidth: COLUMN_WIDTH,
      sortable: true,
      type: 'numericColumn',
      valueFormatter: (params: any) => currencyFormatter(format, params.value),
    },
    valueColumn,
    {
      headerName: 'Cost as % of total IT cost',
      field: 'totalItCost',
      type: 'numericColumn',
      minWidth: COLUMN_WIDTH,
      sortable: true,
      valueFormatter: (params: any) => percentageFormatter(params.value),
    },
  ];
};

const getSpendByProductColumns = ({
  contributions,
  format,
  isHeadcount,
  title,
}: {
  contributions?: string;
  format: ICurrencyFormat;
  isHeadcount?: boolean;
  title: string;
}) => {
  let columnWidth = SPEND_BY_PRODUCT_WIDTH;
  const currencyColumn = {
    sortable: true,
    type: 'numericColumn',
    valueFormatter: (params: any) => currencyFormatter(format, params.value),
  };
  const percentageColumn = {
    type: 'numericColumn',
    sortable: true,
    valueFormatter: (params: any) => percentageFormatter(params.value),
  };
  const thirdPartyColumn = {
    ...currencyColumn,
    headerName: 'Third Party cost',
    field: 'thirdPartySpend',
  };
  const personnelColumn = {
    ...currencyColumn,
    headerName: 'Personnel cost',
    field: 'personnelSpend',
  };
  const totalColumn = {
    ...currencyColumn,
    headerName: 'Total cost',
    field: 'spend',
  };
  const fteColumn = {
    headerName: 'fte',
    field: 'fte',
    type: 'numericColumn',
    sortable: true,
  };
  const allSpendColumn = {
    ...percentageColumn,
    headerName: '% of all App spend',
    field: 'percentageOfAllAppSpend',
  };
  const allITSpendColumn = {
    ...percentageColumn,
    headerName: '% of all IT spend',
    field: 'percentageOfAllITSpend',
  };
  let valueColumns: any[] = [];
  if (isHeadcount) {
    valueColumns = [getHeadcountColumn(), fteColumn];
    columnWidth = (SPEND_BY_PRODUCT_WIDTH * 5) / 2;
  } else if (!contributions) {
    valueColumns = [
      thirdPartyColumn,
      personnelColumn,
      totalColumn,
      allSpendColumn,
      allITSpendColumn,
    ];
    columnWidth = SPEND_BY_PRODUCT_WIDTH;
  } else if (contributions === CONTRIBUTIONS.PERSONNEL) {
    valueColumns = [personnelColumn, allSpendColumn, allITSpendColumn];
    columnWidth = (SPEND_BY_PRODUCT_WIDTH * 5) / 3;
  } else {
    valueColumns = [thirdPartyColumn, allSpendColumn, allITSpendColumn];
    columnWidth = (SPEND_BY_PRODUCT_WIDTH * 5) / 3;
  }
  return [
    {
      headerName: title,
      field: 'name',
      width: 260,
      sortable: true,
    },
    ...valueColumns.map((column) => ({
      ...column,
      width: columnWidth,
    })),
  ];
};
